import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import {
  getComplianceData, getComplianceQuiz, submitComplianceQuiz, saveComplianceProgress,
  GetComplianceDataOutputType, GetComplianceQuizOutputType,
} from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  ShieldCheck, CheckCircle2, Circle, ChevronDown, ChevronUp, BookOpen,
  ClipboardList, PenLine, AlertCircle, Download, Loader2, ExternalLink, FileText, Award,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { buildComplianceCertificateHtml, downloadCertificateFile } from '@/lib/complianceCertificate';

type ComplianceSub = GetComplianceDataOutputType['submissions'][0];
type StudyLink = ComplianceSub['studyLinks'][0];
type ResourceFile = ComplianceSub['resourceFiles'][0];
type QuizQuestion = GetComplianceQuizOutputType['questions'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : ''; } catch { return d || ''; } };

const STEPS = ['Study', 'Quiz', 'Declaration', 'Sign'] as const;

function getStepIndex(status: string, hasQuiz: boolean): number {
  if (status === 'Not Started') return 0;
  if (status === 'Study') return hasQuiz ? 1 : 2;
  if (status === 'Quiz Passed') return 2;
  if (status === 'Declaration') return 3;
  if (status === 'Signed') return 4;
  if (status === 'Overdue') return 0;
  return 0;
}

function StepIndicator({ currentIdx, hasQuiz }: { currentIdx: number; hasQuiz: boolean }) {
  const steps = hasQuiz ? STEPS : ['Study', 'Declaration', 'Sign'];
  return (
    <div className="flex items-center gap-3 mb-5">
      {steps.map((step, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step} className="flex items-center gap-1.5">
            {done
              ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              : <Circle className={`w-4 h-4 shrink-0 ${active ? 'text-foreground' : 'text-muted-foreground'}`} />
            }
            <span className={`text-xs ${done ? 'text-primary' : active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{step}</span>
            {i < steps.length - 1 && <div className="w-6 h-px bg-border mx-1" />}
          </div>
        );
      })}
    </div>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<ComplianceSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{ score: number; passed: boolean; passMark: number } | null>(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [savingDeclaration, setSavingDeclaration] = useState(false);

  const [typedName, setTypedName] = useState('');
  const [typedDob, setTypedDob] = useState('');
  const [signing, setSigning] = useState(false);
  const [savingStudy, setSavingStudy] = useState(false);

  const load = () => {
    if (!user) return;
    getComplianceData({}).then(d => setSubmissions(d.submissions)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  const openForm = async (sub: ComplianceSub) => {
    if (expanded === sub.submissionId) { setExpanded(null); return; }
    setExpanded(sub.submissionId);
    setQuizAnswers({});
    setQuizResult(null);
    setCheckedItems({});
    setTypedName('');
    setTypedDob('');

    const hasQuiz = sub.formType !== 'Working Time Directive';
    const stepIdx = getStepIndex(sub.status, hasQuiz);
    if (hasQuiz && stepIdx === 1 && sub.formId) {
      setQuizLoading(true);
      try {
        const res = await getComplianceQuiz({ formId: sub.formId });
        setQuizQuestions(res.questions);
      } catch { toast.error('Failed to load quiz'); }
      finally { setQuizLoading(false); }
    }
  };

  const markStudied = async (sub: ComplianceSub) => {
    setSavingStudy(true);
    try {
      await saveComplianceProgress({ submissionId: sub.submissionId, step: 'study' });
      toast.success('Study confirmed — proceed to the quiz');
      load();
      if (sub.formType !== 'Working Time Directive') {
        setQuizLoading(true);
        const res = await getComplianceQuiz({ formId: sub.formId });
        setQuizQuestions(res.questions);
        setQuizLoading(false);
      }
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSavingStudy(false); }
  };

  const handleSubmitQuiz = async (sub: ComplianceSub) => {
    if (Object.keys(quizAnswers).length < quizQuestions.length) { toast.error('Please answer all questions'); return; }
    setSubmittingQuiz(true);
    try {
      const answers = Object.entries(quizAnswers).map(([questionId, answer]) => ({ questionId, answer }));
      const res = await submitComplianceQuiz({ formId: sub.formId, submissionId: sub.submissionId, answers });
      setQuizResult({ score: res.score, passed: res.passed, passMark: res.passMark });
      if (res.passed) { toast.success(`Quiz passed with ${res.score}%!`); load(); }
      else { toast.error(`Score: ${res.score}%. Need ${res.passMark}% to pass.`); }
    } catch (e: any) { toast.error(e.message || 'Failed to submit quiz'); }
    finally { setSubmittingQuiz(false); }
  };

  const handleSaveDeclaration = async (sub: ComplianceSub) => {
    const allChecked = sub.declarationItems.every((_, i) => checkedItems[`${sub.submissionId}-${i}`]);
    if (!allChecked) { toast.error('Please confirm all statements'); return; }
    setSavingDeclaration(true);
    try {
      const responses = JSON.stringify(Object.keys(checkedItems).filter(k => k.startsWith(sub.submissionId)));
      await saveComplianceProgress({ submissionId: sub.submissionId, step: 'declaration', declarationResponses: responses });
      toast.success('Declaration saved — please sign below');
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSavingDeclaration(false); }
  };

  const handleSign = async (sub: ComplianceSub) => {
    if (!typedName.trim()) { toast.error('Please type your full name'); return; }
    if (!typedDob.trim()) { toast.error('Please enter your date of birth'); return; }
    setSigning(true);
    try {
      await saveComplianceProgress({ submissionId: sub.submissionId, step: 'signed', typedName: typedName.trim(), dobConfirmation: typedDob.trim() });
      toast.success('Signed and completed! Your certificate is now available.');
      setExpanded(null);
      load();
    } catch (e: any) { toast.error(e.message || 'Date of birth did not match. Please check and try again.'); }
    finally { setSigning(false); }
  };

  const handleDownloadCertificate = (sub: ComplianceSub) => {
    const staffName = sub.typedName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const html = buildComplianceCertificateHtml({
      staffName,
      formName: sub.formName,
      formType: sub.formType,
      quizScore: sub.quizScore,
      passMark: sub.passMark,
      signedAt: sub.signedAt || new Date().toISOString(),
      role: user?.position || user?.role,
      department: user?.department,
    });
    const filename = `Compliance-Certificate-${sub.formName.replace(/\s+/g, '-')}-${staffName.replace(/\s+/g, '-')}.html`;
    downloadCertificateFile(html, filename);
  };

  if (loading) {
    return (
      <div className="px-6 md:px-8 py-8 max-w-3xl mx-auto space-y-3">
        <Skeleton className="h-8 w-64" />
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-4 md:px-8 py-4 md:py-6">
          <h1 className="text-xl font-bold text-foreground">Compliance & Certifications</h1>
          <p className="text-sm text-muted-foreground mt-1">Complete your required compliance training, quizzes and declarations. A certificate is issued upon completion.</p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6 max-w-3xl space-y-3 pb-20 md:pb-6">
        {submissions.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No compliance forms have been assigned to you yet.</p>
          </div>
        ) : (
          submissions.map(sub => {
            const hasQuiz = sub.formType !== 'Working Time Directive';
            const stepIdx = getStepIndex(sub.status, hasQuiz);
            const isExpanded = expanded === sub.submissionId;
            const isOverdue = sub.status !== 'Signed' && sub.deadline && sub.deadline < new Date().toISOString().split('T')[0];
            const today = new Date().toISOString().split('T')[0];
            const isDueSoon = sub.status !== 'Signed' && sub.deadline && sub.deadline >= today && sub.deadline <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

            return (
              <div key={sub.submissionId} className={`border rounded-lg bg-card ${isOverdue ? 'border-destructive/50' : isExpanded ? 'border-primary/30' : 'border-border'}`}>
                {/* Header */}
                <button className="w-full flex items-center justify-between p-4 text-left" onClick={() => openForm(sub)}>
                  <div className="flex items-center gap-3">
                    {sub.status === 'Signed'
                      ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                      : isOverdue
                        ? <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                        : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                    }
                    <div>
                      <p className="text-sm font-medium text-foreground">{sub.formName}</p>
                      <p className="text-xs text-muted-foreground">
                        {sub.status === 'Signed'
                          ? `Completed ${fmtDate(sub.signedAt)}${sub.nextDueDate ? ` · Next due ${fmtDate(sub.nextDueDate)}` : ''}`
                          : sub.deadline ? `Due ${fmtDate(sub.deadline)}` : sub.formType
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {sub.status === 'Signed' && <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-sm">✓ Certified</span>}
                    {isOverdue && <span className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-sm">Overdue</span>}
                    {isDueSoon && !isOverdue && sub.status !== 'Signed' && <span className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-500 rounded-sm">Due Soon</span>}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {/* Expanded — In Progress */}
                {isExpanded && sub.status !== 'Signed' && (
                  <div className="border-t border-border p-4">
                    <StepIndicator currentIdx={stepIdx} hasQuiz={hasQuiz} />

                    {sub.consequenceText && (
                      <div className="flex items-start gap-2 bg-destructive/5 border border-destructive/20 rounded px-3 py-2 mb-4 text-xs text-destructive">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {sub.consequenceText}
                      </div>
                    )}

                    {/* STEP 1: Study */}
                    {stepIdx === 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Study Materials</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">Please read all the materials below carefully before proceeding to the quiz.</p>
                        {sub.formBody && (
                          <div className="bg-muted/40 border border-border rounded p-3 text-xs text-foreground mb-4 max-h-56 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                            {sub.formBody}
                          </div>
                        )}
                        {(sub.resourceFiles.length > 0 || sub.studyLinks.length > 0) && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Reference Resources</p>
                            <div className="space-y-1.5">
                              {sub.resourceFiles.map((file: ResourceFile, i: number) => (
                                <a key={i} href={file.url} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2.5 bg-muted/40 hover:bg-muted border border-border rounded px-3 py-2 text-xs transition-colors group">
                                  <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                                  <span className="flex-1 text-foreground font-medium truncate">{file.name}</span>
                                  <Download className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                                </a>
                              ))}
                              {sub.studyLinks.map((link: StudyLink, i: number) => (
                                link.url ? (
                                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-2.5 bg-muted/40 hover:bg-muted border border-border rounded px-3 py-2 text-xs transition-colors group">
                                    <ExternalLink className="w-3.5 h-3.5 text-primary shrink-0" />
                                    <span className="flex-1 text-foreground font-medium truncate">{link.label || link.url}</span>
                                  </a>
                                ) : (
                                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground px-1"><span>•</span> {link.label}</div>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                        <Button onClick={() => markStudied(sub)} disabled={savingStudy} className="bg-primary text-primary-foreground">
                          {savingStudy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving...</> : "I've reviewed these materials →"}
                        </Button>
                      </div>
                    )}

                    {/* STEP 2: Quiz */}
                    {hasQuiz && stepIdx === 1 && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <ClipboardList className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Quiz{sub.passMark ? ` — Pass mark: ${sub.passMark}%` : ''}</h3>
                        </div>
                        {quizResult && !quizResult.passed && (
                          <div className="bg-destructive/5 border border-destructive/20 rounded p-3 mb-4 text-sm text-destructive">
                            Score: {quizResult.score}% — Required: {quizResult.passMark}%. Please review the materials and try again.
                          </div>
                        )}
                        {quizLoading ? (
                          <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
                        ) : quizQuestions.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">No quiz questions have been added yet.</p>
                        ) : (
                          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                            {quizQuestions.map((q, qi) => (
                              <div key={q.id} className="border border-border rounded p-3">
                                <p className="text-sm font-medium text-foreground mb-2">Q{qi + 1}: {q.question}</p>
                                <div className="space-y-1.5">
                                  {(['A', 'B', 'C', 'D'] as const).map(opt => {
                                    const val = q[`option${opt}` as keyof QuizQuestion] as string;
                                    if (!val) return null;
                                    const selected = quizAnswers[q.id] === opt;
                                    return (
                                      <button key={opt} onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                        className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-foreground hover:bg-muted'}`}>
                                        <span className="font-medium mr-2">{opt})</span>{val}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {quizQuestions.length > 0 && (
                          <Button onClick={() => handleSubmitQuiz(sub)} disabled={submittingQuiz || Object.keys(quizAnswers).length < quizQuestions.length} className="mt-4 bg-primary text-primary-foreground">
                            {submittingQuiz ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Submitting...</> : 'Submit Quiz'}
                          </Button>
                        )}
                      </div>
                    )}

                    {/* STEP 3: Declaration */}
                    {stepIdx === 2 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <ClipboardList className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Declaration</h3>
                        </div>
                        {sub.formType === 'DPS Authorisation' && sub.dpsHolderName && (
                          <div className="bg-muted/40 rounded p-3 text-xs text-muted-foreground mb-4">
                            <p className="font-medium text-foreground mb-1">DPS Authorisation</p>
                            <p>I, <strong>{sub.dpsHolderName}</strong>{sub.dpsLicenceNumber ? `, personal licence number ${sub.dpsLicenceNumber}` : ''}{sub.premisesLicenceNumber ? `, premises licence number ${sub.premisesLicenceNumber}` : ''}, hereby authorise <strong>{user?.firstName} {user?.lastName}</strong>, {user?.position || 'employee'}, to sell and supply alcohol under my authority.</p>
                          </div>
                        )}
                        {sub.declarationItems.length > 0 && (
                          <div className="space-y-2.5 mb-4">
                            <p className="text-xs font-medium text-foreground">Please read and confirm each statement:</p>
                            {sub.declarationItems.map((item, i) => {
                              const key = `${sub.submissionId}-${i}`;
                              return (
                                <div key={i} className="flex items-start gap-2.5">
                                  <Checkbox id={key} checked={!!checkedItems[key]} onCheckedChange={v => setCheckedItems(prev => ({ ...prev, [key]: !!v }))} className="mt-0.5" />
                                  <label htmlFor={key} className="text-xs text-muted-foreground cursor-pointer leading-relaxed">{item}</label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <Button onClick={() => handleSaveDeclaration(sub)} disabled={savingDeclaration} className="bg-primary text-primary-foreground">
                          {savingDeclaration ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving...</> : 'Confirm Declaration →'}
                        </Button>
                      </div>
                    )}

                    {/* STEP 4: Sign */}
                    {stepIdx === 3 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <PenLine className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Digital Signature</h3>
                        </div>
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                          <p className="text-xs font-semibold text-primary mb-1">🏆 Certificate Ready</p>
                          <p className="text-xs text-muted-foreground">Once you sign below, a Certificate of Competence will be issued in your name, confirming you have completed all training and passed the assessment.</p>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">By signing below, you confirm you have read, understood, and agree to all the statements above. Type your full name and date of birth to verify your identity.</p>
                        <div className="space-y-3 mb-4">
                          <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Full Name *</label>
                            <Input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()} />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Date of Birth (DD/MM/YYYY) *</label>
                            <Input value={typedDob} onChange={e => setTypedDob(e.target.value)} placeholder="e.g. 14/03/1990" />
                            <p className="text-xs text-muted-foreground mt-1">Must match the date of birth on your records</p>
                          </div>
                        </div>
                        <Button onClick={() => handleSign(sub)} disabled={signing || !typedName.trim() || !typedDob.trim()} className="bg-primary text-primary-foreground">
                          {signing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Signing...</> : 'Sign & Generate Certificate ✓'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Completed — Certificate Download */}
                {isExpanded && sub.status === 'Signed' && (
                  <div className="border-t border-border p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Award className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">Certificate of Competence Issued</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Completed by {sub.typedName} on {fmtDate(sub.signedAt)}
                            {sub.quizScore != null && <span className="ml-2 font-semibold text-green-600">· Score: {sub.quizScore}%</span>}
                          </p>
                          {sub.nextDueDate && <p className="text-xs text-muted-foreground mt-0.5">Renewal due: {fmtDate(sub.nextDueDate)}</p>}
                        </div>
                      </div>
                      <Button size="sm" className="gap-1.5 shrink-0" onClick={() => handleDownloadCertificate(sub)}>
                        <Award className="w-3.5 h-3.5" /> Download Certificate
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
